/* eslint-disable */
import { Select, Typography } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

import useDebounceFn from 'hooks/useDebounceFn';
import useScrollInfinite from 'hooks/useScrollInfinite';
import { getProvinceByBankNameService } from 'services/banks';

export type DropdownElementProps = {
  options?: OptionType[];
  loading?: boolean;
  name?: string;
  placeholder?: string;
  size?: SizeType;
  value: any;
  onChange: (...event: any[]) => void;
  filterParams?: string;
  isGetOption?: boolean;
  hasAllOption?: boolean;
  isShowSearch?: boolean;
  multiple?: {
    allowClear?: boolean;
    defaultValue?: any;
  },
  error?: string;
  ids?: string;
  slugs?: string;
  labelInValue?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  noMargin?: boolean;
  allowClear?: boolean;
  handleLoadMore?: () => void;
  enabled?: boolean;
  bankNameWatch: OptionType | null;
  enableQuery?: boolean
};

export const DropdownCity: React.FC<DropdownElementProps> = ({
  name,
  loading,
  options,
  size,
  placeholder,
  isShowSearch = true,
  value,
  onChange,
  filterParams,
  hasAllOption,
  isGetOption,
  multiple,
  error,
  ids,
  slugs,
  labelInValue = false,
  disabled,
  readOnly,
  noMargin,
  className,
  allowClear = true,
  bankNameWatch,
  enableQuery
}) => {
  const { t } = useTranslation();
  const [txtSearch, setTxtSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [optionsData, setOptionsData] = useState<OptionType[]>(options || []);

  useDebounceFn(() => {
    setSearchDebounce(txtSearch);
  }, 500, [txtSearch]);

  const {
    data: dropdownList,
    isLoading,
    fetchNextPage,
  } = useInfiniteQuery(
    [`dropdown-city-${name ? `-${name}` : ''}`, {
      searchDebounce, ids, slugs
    }, bankNameWatch?.label],
    ({ pageParam = 1 }) => getProvinceByBankNameService({
      page: pageParam,
      limit: 15,
      ...bankNameWatch && { _name: bankNameWatch.label },
    }),
    {
      enabled: enableQuery,
      getNextPageParam: (lastPage) => (lastPage.meta.page + 1 <= lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined),
    },
  );

  const { setNode } = useScrollInfinite(() => {
    fetchNextPage();
  });

  useEffect(() => {
    if (dropdownList) {
      const data = dropdownList.pages.reduce((prev: OptionType[], current) => [...prev,
      ...current.data.map((val) => ({
        label: val.province?.name || '',
        value: val.province?.code,
      })).filter((val) => ((filterParams ? val?.value?.toString() !== filterParams : true)))], []);
      setOptionsData((hasAllOption && !searchDebounce) ? [...data] : data);
    }
  }, [dropdownList, filterParams, hasAllOption, searchDebounce]);

  useEffect(() => {
    if (options) {
      setOptionsData(options);
    }
  }, [options]);

  return (
    <>
      <Select
        className={`${!noMargin && 'u-mt-8'}${readOnly ? ' readonly' : ''} ${className}`}
        disabled={disabled || readOnly}
        size={size}
        style={{ width: '100%' }}
        placeholder={disabled || readOnly ? '' : placeholder}
        value={value}
        labelInValue={labelInValue}
        onChange={(val) => {
          if (isGetOption) {
            if (multiple) {
              const selected = optionsData.filter((item) => val.includes(item.value));
              onChange(selected);
            } else if (val) {
              onChange(optionsData.find((item) => item.value === val));
            } else {
              onChange({
                value: undefined,
                label: undefined
              });
            }
          } else {
            onChange(val);
          }
          if (multiple) {
            setTxtSearch('');
          }
        }}
        {...multiple && {
          mode: 'multiple',
          allowClear: multiple.allowClear,
          defaultValue: multiple.defaultValue,
        }}
        dropdownMatchSelectWidth
        getPopupContainer={(trigger) => trigger.parentElement}
        defaultActiveFirstOption={false}
        showSearch={isShowSearch}
        filterOption={false}
        onSearch={(val) => isShowSearch && setTxtSearch(val)}
        onClear={() => setTxtSearch('')}
        allowClear={allowClear}
        loading={isLoading || loading}
      >
        {
          optionsData.map(
            (option, idx) => (idx === optionsData.length - 3 ? (
              <Select.Option
                label={t(option.label)}
                value={option.value}
                key={`option-${idx.toString()}`}
                disabled={option.disabled}
              >
                <div ref={(ref) => setNode(ref)}>{t(option.label)}</div>
              </Select.Option>
            ) : (
              <Select.Option
                label={t(option.label)}
                value={option.value}
                key={`option-${idx.toString()}`}
                disabled={option.disabled}
              >
                {t(option.label)}
              </Select.Option>
            ))
          )
        }
      </Select>
      {error && (
        <Typography.Text type="danger">
          {error}
        </Typography.Text>
      )}
    </>
  );
};
